import { memo, useMemo } from 'react';

import Chip from '@mui/material/Chip';

import { SlotTableActionsColumn } from './slotsTableColumns/SlotTableActionsColumn';
import { SlotTableActiveColumn } from './slotsTableColumns/SlotTableActiveColumn';
import { SlotTableMarksColumn } from './slotsTableColumns/SlotTableMarksColumn';
import { SlotTableOrderColumn } from './slotsTableColumns/SlotTableOrderColumn';

import css from './SlotsTable.module.css';

import { DataTable } from 'v2/components/dataTable/DataTable';
import { ImageButton } from 'v2/components/imageButton/ImageButton';
import { IPagination, Pageable, TableColumn } from 'v2/types';
import { SlotModel } from 'v2/types/SlotModel';
import { fileSrc } from 'v2/utils';

interface SlotsTableProps {
  data?: Pageable<SlotModel>;
  pagination: IPagination;
  onRevalidate: VoidFunction;
}

export const SlotsTable = memo<SlotsTableProps>(({ data, pagination, onRevalidate }) => {
  const columns: TableColumn<SlotModel>[] = useMemo(
    () => [
      {
        key: 'isActive',
        label: 'Active',
        align: 'center',
        style: { width: '5%' },
        render: (row) => <SlotTableActiveColumn slot={row} />,
      },
      {
        key: 'order',
        align: 'center',
        label: 'Order',
        style: { width: '6%' },
        render: (row) => <SlotTableOrderColumn slot={row} onRevalidate={onRevalidate} />,
      },
      {
        key: 'icon',
        label: 'Icon',
        style: { width: '8%' },
        render: (row) => <ImageButton className={css.slotsTableImage} src={fileSrc(row.iconId)} alt={row.title} />,
      },
      {
        id: 'title',
        key: 'title',
        label: 'Title',
        ellipsis: true,
        style: { width: '15%' },
      },
      {
        key: 'providerName',
        label: 'Provider',
        style: { width: '10%' },
        render: (row) => <Chip label={row.providerName} size="small" />,
      },
      {
        key: 'marks',
        label: 'Marks',
        style: { width: '20%' },
        render: (row) => <SlotTableMarksColumn slot={row} />,
      },
      {
        key: 'actions',
        align: 'right',
        style: { width: '5%' },
        render: (row) => <SlotTableActionsColumn slot={row} onRevalidate={onRevalidate} />,
      },
    ],
    [onRevalidate]
  );

  return (
    <DataTable
      columns={columns}
      data={data?.result}
      total={data?.total}
      page={pagination.page}
      pageSize={pagination.pageSize}
      onPageChange={pagination.onPageChange}
      withPagination
    />
  );
});

SlotsTable.displayName = 'SlotsTable';
